import { idiv, imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt } from '../math/rng.ts';
import type { BattleState } from '../types.ts';
import { damageHero, ev, isFlying, spawnEnemy } from './combat.ts';
import { heartCarrier, sunkenWave } from './sunken.ts';

/**
 * The fin (La Orilla 2, docs/SAGAS.md): the titan moves its fore-fin in its sleep, as if swimming, and every time a
 * wave comes over the stilt-houses. Every so often one of the lanes that run in from the sea is marked (the water draws
 * back, the fin's shadow rises) and then the wave takes everything in it: nightmares are swept away, and a hero not
 * holding on to a post there is thrown out of it, hurt. By day there are few nightmares; at night the Drowned come up
 * in a mass, and the wave is what is left to hold them with. Dawn wins it; the stilt-houses falling lose it.
 *
 * La Orilla 5 uses the same lanes twice. In its first fight (`breath`) it is the dragon's fire that comes down the
 * marked lane, from the sea: it burns the heroes in it whether they hold a post or not, and leaves the nightmares; with
 * the heart taken it is the carrier's lane it marks. In the last, the wave crushes the Drowned Dragon (battle/sunken.ts).
 */

/** Along a lane (from the sea in) and across it, for a point: `dir` is the way in from the sea (thousandths). */
const across = (s: BattleState, lane: number, x: number, y: number): number => {
  const r = s.rules?.fin;
  if (!r) return 0;
  // The lanes run along `dir`; across them is `dir` turned a quarter.
  return idiv(-x * r.dir.y + y * r.dir.x, 1000) - (r.lanes[lane] ?? 0);
};

/** Whether a point is in lane `lane`. */
export const inLane = (s: BattleState, lane: number, x: number, y: number): boolean => {
  const r = s.rules?.fin;
  if (!r) return false;
  const a = across(s, lane, x, y);
  return a >= -r.half && a <= r.half;
};

export const finTick = (s: BattleState): void => {
  const r = s.rules?.fin;
  if (!r) return;
  const st = (s.fin ??= { next: r.first, lane: -1, count: 0, done: 0 });
  if (!s.started || st.done) return;
  // The lane to come is marked `warn` before the fin comes down.
  if (st.lane < 0 && s.waveT >= st.next - r.warn) {
    st.lane = (st.count + rngInt(s.rng, r.lanes.length)) % r.lanes.length;
    // The dragon's fire looks for its heart.
    const c = r.breath ? heartCarrier(s) : undefined;
    const at = c ? r.lanes.findIndex((_, i) => inLane(s, i, c.x, c.y)) : -1;
    if (at >= 0) st.lane = at;
    ev(s, { t: 'fin', kind: 'warn', lane: st.lane, x: 0, y: 0 });
  }
  if (st.lane >= 0 && s.waveT >= st.next) {
    const lane = st.lane;
    if (!r.breath) for (const e of s.enemies) if (e.hp > 0 && e.kind !== 'boneWall' && e.kind !== 'sunken' && !isFlying(e) && inLane(s, lane, e.x, e.y)) e.hp = 0;
    sunkenWave(s, (x, y) => inLane(s, lane, x, y));
    for (const h of s.heroes) {
      if (h.downTicks > 0 || !inLane(s, lane, h.x, h.y)) continue;
      if (r.breath) {
        damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.hitBp)), null);
        continue;
      }
      if (r.posts.some((p) => withinRange(h.x, h.y, p.x, p.y, r.hold))) continue;
      damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.hitBp)), null);
      // Thrown out of the lane, on the side it was nearer.
      const a = across(s, lane, h.x, h.y);
      const push = (a >= 0 ? r.half - a : -r.half - a) + (a >= 0 ? r.throw : -r.throw);
      h.x += idiv(-r.dir.y * push, 1000);
      h.y += idiv(r.dir.x * push, 1000);
      h.moving = false;
      h.path = [];
      if (h.grabbed) h.grabbed = 0;
    }
    ev(s, { t: 'fin', kind: 'sweep', lane, x: 0, y: 0 });
    st.count++;
    st.lane = -1;
    st.next = s.waveT + (s.waveT >= r.night ? r.everyNight : r.every);
  }
  // At night the Drowned come up out of the sea in a mass.
  if (r.shore.length && s.waveT >= r.night && s.waveT < r.dawn - r.calm && (s.waveT - r.night) % r.drowned.every === 0) {
    for (let i = 0; i < (r.drowned.batch ?? 1); i++) {
      const m = r.shore[rngInt(s.rng, r.shore.length)];
      if (m) spawnEnemy(s, 'drowned', 1, false, 0, m.x, m.y, mulBp(r.drowned.hp, s.rules?.enemyHpBp ?? 10000));
    }
  }
  if (s.waveT >= r.dawn) {
    st.done = s.tick;
    ev(s, { t: 'fin', kind: 'dawn', lane: -1, x: 0, y: 0 });
  }
};

/** Dawn: won; the stilt-houses fallen: lost. */
export const finResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.fin;
  if (!r) return null;
  if (s.keep.hp <= 0) return 'lost';
  return s.fin?.done && s.tick - s.fin.done >= r.beat ? 'won' : null;
};
